import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Checkbox,
  Step,
  StepLabel,
  StepContent,
  Box,
  CircularProgress,
  Button,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

import SuiInput from "components/SuiInput";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";

import VerificacionCausa, { validarCausa } from "./components/verificacionCausa";
import VerificacionDeuda, { validateDeudas } from "./components/verificacionDeuda";
import VerificacionAnexos, { validateAnexos } from "./components/verificacionAnexos";
import VerificacionProyeccion, { validateProyeccion } from "./components/verificarProyeccion";
import FileDialog from "components/FileDialog";
import PDFApi from "api/pdfApi";
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";
import PlanesPagoApi from "api/planesPagoApi";
import { styles } from "./styles";

import SelectApoderado from "../../../resumen/components/SelectApoderado";

// Icono para los pasos
const StepIcon = ({ completed, error, active }) => {
  if (active) {
    return <CircularProgress size={20} color="info" />;
  }
  if (completed) {
    return <CheckCircleIcon sx={{ color: "success.main" }} />;
  }
  if (error) {
    return <CancelIcon sx={{ color: "error.main" }} />;
  }
  return <Box sx={{ width: 20, height: 20 }} />;
};

StepIcon.propTypes = {
  completed: PropTypes.bool,
  error: PropTypes.bool,
  active: PropTypes.bool,
};

// Resultado de las Validaciones
const StepResult = ({
  status = null, // Movemos el valor por defecto aquÃ­
  type,
}) => {
  if (!status?.results) return null;
  const { results, completed } = status;

  if (type === "causas" && !completed) {
    const { causas } = results.results;
    return (
      <SuiBox sx={styles.resultContainer}>
        <SuiTypography variant="caption" fontWeight="medium" style={{ color: "#ff0000" }}>
          Causas de la Solicitud tiene
          {causas.palabras > 1 ? ` ${causas.palabras} palabras` : " 1 palabra"}, el mÃ­nimo
          requerido es de {causas.palabrasMinimas} palabras
        </SuiTypography>
      </SuiBox>
    );
  }

  if (type === "deudas" && !completed) {
    const {
      acreedoresFaltantes = 0,
      porcentajeEnMora = 0,
      montoTotalEnMora = 0,
      porcentajeFaltante = 0,
      montoFaltante = 0,
      porcentajeMinimo = 30,
    } = results;

    return (
      <SuiBox sx={styles.resultContainer}>
        {acreedoresFaltantes > 0 && (
          <SuiTypography variant="caption" fontWeight="medium" style={{ color: "#ff0000" }}>
            {acreedoresFaltantes === 1
              ? "Falta 1 acreedor en mora"
              : `Faltan ${acreedoresFaltantes} acreedores en mora`}
          </SuiTypography>
        )}

        {porcentajeEnMora < porcentajeMinimo && (
          <SuiTypography variant="caption" fontWeight="medium" style={{ color: "#ff0000" }}>
            Actualmente tiene {porcentajeEnMora.toFixed(2)}% en mora (${" "}
            {montoTotalEnMora.toLocaleString()}). Falta {porcentajeFaltante.toFixed(2)}% (${" "}
            {montoFaltante.toLocaleString()}) para alcanzar el mínimo del {porcentajeMinimo}% del
            total de deudas
          </SuiTypography>
        )}
      </SuiBox>
    );
  }

  if (type === "proyeccion") {
    const { mensajeError = "", advertencias = [] } = results;
    if (completed && advertencias.length === 0) return null;
    return (
      <SuiBox sx={styles.resultContainer}>
        {!completed && mensajeError && (
          <SuiTypography
            variant="caption"
            fontWeight="medium"
            style={{ color: "#ff0000" }}
            display="block"
          >
            {mensajeError}
          </SuiTypography>
        )}
        {advertencias.map((advertencia) => (
          <SuiTypography
            key={advertencia}
            variant="caption"
            fontWeight="medium"
            style={{ color: "#f57c00" }}
            display="block"
          >
            ⚠ {advertencia}
          </SuiTypography>
        ))}
      </SuiBox>
    );
  }

  if (type === "anexos" && !completed) {
    const { documentosFaltantes = [] } = results;
    return (
      <SuiBox sx={styles.resultContainer}>
        {documentosFaltantes.map((doc, index) => (
          <SuiTypography
            key={index}
            variant="caption"
            fontWeight="medium"
            style={{ color: "#ff0000" }}
            display="block"
          >
            Falta: {doc.tipo}
            {doc.detalle ? ` - ${doc.detalle}` : ""}
          </SuiTypography>
        ))}
      </SuiBox>
    );
  }

  return null;
};

StepResult.propTypes = {
  status: PropTypes.shape({
    completed: PropTypes.bool,
    results: PropTypes.shape({
      results: PropTypes.shape({
        causas: PropTypes.shape({
          palabras: PropTypes.number,
          palabrasMinimas: PropTypes.number,
        }),
      }),
      porcentajeEnMora: PropTypes.number,
      porcentajeFaltante: PropTypes.number,
      montoFaltante: PropTypes.number,
      porcentajeMinimo: PropTypes.number,
      montoTotalEnMora: PropTypes.number,
      acreedoresMora: PropTypes.number,
      acreedoresFaltantes: PropTypes.number,
      cumpleMinAcreedores: PropTypes.bool,
      mensajeError: PropTypes.string,
      advertencias: PropTypes.arrayOf(PropTypes.string),
      validaciones: PropTypes.arrayOf(
        PropTypes.shape({
          tipo: PropTypes.string,
          presente: PropTypes.bool,
          requerido: PropTypes.bool,
          detalle: PropTypes.string,
        })
      ),
      documentosFaltantes: PropTypes.arrayOf(
        PropTypes.shape({
          tipo: PropTypes.string,
          detalle: PropTypes.string,
        })
      ),
    }),
  }),
  type: PropTypes.oneOf(["descripcion", "deudas", "anexos", "proyeccion"]).isRequired, // Agregamos 'proyeccion' aquÃ­
};

export { StepResult };

const verificationSteps = [
  {
    key: "valid_causa",
    label: "1. Validación de la Causa de la Solicitud ",
    description: "1.1. Validación de la Longitud del Campo: Causas de la Solicitud",
    component: VerificacionCausa,
    validator: validarCausa,
    type: "causas",
  },
  {
    key: "valid_deudas",
    label: "2. Validación de Deudas",
    description: "2.1. Validación del Porcentaje y Cantidad de Deudas en Mora",
    component: VerificacionDeuda,
    validator: validateDeudas,
    type: "deudas",
  },
  {
    key: "valid_proyeccion",
    label: "3. Validación de Proyección",
    description: "3.1. Validacion de Existencia de Proyección",
    component: VerificacionProyeccion,
    validator: validateProyeccion,
    type: "proyeccion",
  },
  {
    key: "valid_anexos",
    label: "4. Validación de Anexos",
    description: "4.1. Verificación de documentos requeridos",
    component: VerificacionAnexos,
    validator: validateAnexos,
    type: "anexos",
  },
];

const VerificacionForm = React.forwardRef(function VerificacionForm(
  { procesoId, onFinish = null },
  ref
) {
  const [stepsStatus, setStepsStatus] = useState({});
  const [canalizarSolicitud, setCanalizarSolicitud] = useState(false);
  const [obserAutorizacion, setObserAutorizacion] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [pdfExists, setPdfExists] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [procesoData, setProcesoData] = useState(null);
  const [validacionFallida, setValidacionFallida] = useState(false);
  const [errorObservacion, setErrorObservacion] = useState("");
  const [showApoderadoModal, setShowApoderadoModal] = useState(false);
  const [apoderadoDeudor, setApoderadoDeudor] = useState(null);

  // Estados para comprobante de pago
  const [planPagoData, setPlanPagoData] = useState(null);
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [uploadingComprobante, setUploadingComprobante] = useState(false);
  const [comprobanteError, setComprobanteError] = useState("");
  const [comprobanteSuccess, setComprobanteSuccess] = useState("");
  const [comprobantesExistentes, setComprobantesExistentes] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loadingComprobantes, setLoadingComprobantes] = useState(false);

  const FILE_NAME = "pdf_solicitud.pdf";
  const ANEXO_TYPE = "PDF_SOLICITUD";

  useEffect(() => {
    const fetchProcesoData = async () => {
      try {
        if (procesoId) {
          const response = await ProcesosApi.fetchProcesoById(procesoId);
          setProcesoData(response.data);
          // Verificamos si el proceso ya está validado y en estado 1
          if (response.data?.validado && response.data?.estado === 1) {
            setCanalizarSolicitud(true);
            setValidacionFallida(true); // Para que se muestre el checkbox
          }
          if (response.data?.identificador) {
            checkPdfExists(response.data.identificador);
            await fetchPlanPagoData(response.data.identificador);
          }
          if (response.data?.apoderado) {
            setApoderadoDeudor(response.data?.apoderado_detalle);
          }
        }
      } catch (error) {
        console.error("Error obteniendo datos del Proceso:", error);
      }
    };

    fetchProcesoData();
  }, [procesoId]);

  const fetchComprobantesExistentes = async (procesoIdentificador, planPagoId) => {
    if (!planPagoId) return;
    setLoadingComprobantes(true);
    try {
      const response = await BucketApi.getAllPlanPagoImages(procesoIdentificador, planPagoId);
      if (response.data.success) {
        const comprobantes = response.data.files
          .filter((fileName) => fileName.startsWith("comprobante_pago_"))
          .map((fileName) => ({
            fileName: fileName,
            displayName: fileName.replace("comprobante_pago_", "").replace(".pdf", ""),
            extension: fileName.split(".").pop(),
          }));
        setComprobantesExistentes(comprobantes);
      }
    } catch (error) {
      console.error("Error al obtener comprobantes:", error);
    } finally {
      setLoadingComprobantes(false);
    }
  };

  const fetchPlanPagoData = async (procesoIdentificador) => {
    try {
      const response = await PlanesPagoApi.obtenerPlanPagoByProcesoIdentificador(
        procesoIdentificador
      );
      if (response.data) {
        const planActivo = response.data;
        setPlanPagoData(planActivo);
        await fetchComprobantesExistentes(procesoIdentificador, planActivo.id);
      }
    } catch (error) {
      console.error("Error obteniendo datos del plan de pago:", error);
    }
  };

  const getNextConsecutivo = (tipoArchivo, archivosExistentes) => {
    const archivosDelTipo = archivosExistentes.filter((archivo) =>
      archivo.fileName.startsWith(`${tipoArchivo}_`)
    );
    let maxConsecutivo = 0;
    archivosDelTipo.forEach((archivo) => {
      const match = archivo.fileName.match(new RegExp(`^${tipoArchivo}_(\\d+)\\.`));
      if (match) {
        const consecutivo = parseInt(match[1], 10);
        if (consecutivo > maxConsecutivo) {
          maxConsecutivo = consecutivo;
        }
      }
    });
    return maxConsecutivo + 1;
  };

  const handleComprobanteFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ["application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        setComprobanteError("Solo se permiten archivos PDF");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setComprobanteError("El archivo no puede superar los 10MB");
        return;
      }
      setComprobanteFile(file);
      setComprobanteError("");
      setComprobanteSuccess("");
    }
  };

  // Función para subir el comprobante de pago
  const handleUploadComprobante = async () => {
    if (!comprobanteFile) {
      setComprobanteError("Seleccione un archivo de comprobante");
      return;
    }
    if (!planPagoData?.id) {
      setComprobanteError("No se encontró un plan de pago asociado al proceso");
      return;
    }
    try {
      setUploadingComprobante(true);
      setComprobanteError("");
      setComprobanteSuccess("");
      // Obtener archivos existentes para calcular consecutivo
      const response = await BucketApi.getAllPlanPagoImages(
        procesoData.identificador,
        planPagoData.id
      );
      let archivosExistentes = [];
      if (response.data.success) {
        archivosExistentes = response.data.files.map((fileName) => ({
          fileName: fileName,
        }));
      }
      // Calcular el siguiente consecutivo para comprobantes de pago
      const consecutivo = getNextConsecutivo("comprobante_pago", archivosExistentes);
      // Generar nombre con consecutivo
      const extension = comprobanteFile.name.split(".").pop();
      const fileName = `comprobante_pago_${consecutivo.toString().padStart(3, "0")}.${extension}`;
      // Subir el archivo
      const uploadResponse = await BucketApi.uploadPlanPagoImage(
        comprobanteFile,
        fileName,
        procesoData.identificador,
        planPagoData.id
      );
      if (uploadResponse.data && uploadResponse.data.file_url) {
        setComprobanteSuccess(`Comprobante subido exitosamente`);
        setComprobanteFile(null);
        // Limpiar el input file
        const fileInput = document.getElementById("comprobante-upload");
        if (fileInput) fileInput.value = "";
        await fetchComprobantesExistentes(procesoData.identificador, planPagoData.id);
      } else {
        throw new Error("No se pudo obtener la URL del archivo subido");
      }
    } catch (error) {
      console.error("Error al subir el comprobante:", error);
      setComprobanteError("Error al subir el comprobante. Intente nuevamente.");
    } finally {
      setUploadingComprobante(false);
    }
  };

  const handleViewComprobante = async (comprobante) => {
    try {
      const response = await BucketApi.getPlanPagoFileUrlFresh(
        comprobante.fileName,
        procesoData.identificador,
        planPagoData.id
      );

      if (response.data.exists && response.data.file_url) {
        window.open(response.data.file_url, "_blank");
      } else {
        setComprobanteError("El comprobante no está disponible");
      }
    } catch (error) {
      console.error("Error al obtener la URL del comprobante:", error);
      setComprobanteError("Error al acceder al comprobante");
    }
  };

  const handleDeleteComprobante = async (comprobante) => {
    const confirmDelete = window.confirm(
      `¿Está seguro de que desea eliminar el comprobante "${comprobante.displayName}"?`
    );
    if (!confirmDelete) return;
    try {
      await BucketApi.deletePlanPagoImage(
        comprobante.fileName,
        procesoData.identificador,
        planPagoData.id
      );
      await fetchComprobantesExistentes(procesoData.identificador, planPagoData.id);
      setComprobanteSuccess(`Comprobante "${comprobante.displayName}" eliminado exitosamente`);
    } catch (error) {
      console.error("Error al eliminar el comprobante:", error);
      setComprobanteError("Error al eliminar el comprobante");
    }
  };

  const actualizarEstadoProceso = async (validacionExitosa) => {
    try {
      const updateData = {
        id_proceso: procesoId,
        operacion: "VALIDAR_PROCESO",
        obser_validacion: obserAutorizacion,
      };
      if (validacionExitosa) {
        // Validación automatica exitosa - cambia estado a PENDIENTE (2)
        await ProcesosApi.operarProceso({ ...updateData, estado: 2 });
      } else if (canalizarSolicitud) {
        // Validación manual con check Canalizar activo - mantiene estado en BORRADOR (1)
        if (!obserAutorizacion.trim()) {
          setErrorObservacion("La observación es obligatoria cuando se asigna a coordinadores");
          return false;
        }
        // No se envia el campo ESTADO y se mantiene estado en BORRADOR (1)
        await ProcesosApi.operarProceso({ ...updateData, canalizar: canalizarSolicitud });
      }
      let alertMensaje = `La solicitud ${procesoData.identificador} se ha actualizada satisfactoriamente. La solicitud fue asignada al grupo de COORDINADORES.`;
      alert(alertMensaje);
      return true;
    } catch (error) {
      console.error("Error al actualizar el estado del proceso:", error);
      return false;
    }
  };

  const checkPdfExists = async (identificador) => {
    try {
      const response = await BucketApi.checkProcesoFileExists(FILE_NAME, identificador);
      const exists = response.data.exists;
      setPdfExists(exists);
      if (exists) {
        //const urlResponse = await BucketApi.getProcesoFileUrl(FILE_NAME, identificador);
        const urlResponse = await BucketApi.getProcesoFileUrlFresh(FILE_NAME, identificador);
        setPdfUrl(urlResponse.data.file_url);
      }
      return exists;
    } catch (error) {
      console.error("Error verificando PDF:", error);
      setPdfExists(false);
      return false;
    }
  };

  const handleValidarSolicitud = async () => {
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea validar la Solicitud? Si la Solicitud supera todas las validaciones, ésta será asignada al grupo de COORDINADORES.`
    );
    if (!confirmacion) return;
    setIsValidating(true);
    setStepsStatus({});
    setValidacionFallida(false);
    setErrorObservacion("");

    try {
      const validationResults = await Promise.all(
        verificationSteps.map(async (step, index) => {
          setCurrentStep(index);
          try {
            let validation = await step.validator(procesoId);
            setStepsStatus((prev) => ({
              ...prev,
              [index]: {
                completed: validation.completed,
                error: validation.error,
                errorMessage: validation.mensajeError || "",
                results: validation.results,
              },
            }));
            return validation;
          } catch (error) {
            console.error(`Error en paso ${index}:`, error);
            return { completed: false, error: true };
          }
        })
      );

      const todasValidacionesExitosas = validationResults.every(
        (result) => result.completed && !result.error
      );
      if (todasValidacionesExitosas) {
        await actualizarEstadoProceso(true);
      } else {
        setValidacionFallida(true);
      }
    } catch (error) {
      console.error("Error en la validación:", error);
      setValidacionFallida(true);
    } finally {
      setCurrentStep(null);
      setIsValidating(false);
    }
  };

  const handleFinish = async () => {
    const confirmacion = window.confirm(
      `¿Está seguro(a) de que desea acabar el registro de la Solicitud? Si la Solicitud supera todas las validaciones, ésta será asignada al grupo de COORDINADORES.`
    );
    if (!confirmacion) return;
    try {
      if (!pdfExists) {
        alert(
          "Debe haber generado el PDF de la Solicitud para finalizar el registro de la solicitud."
        );
        return false;
      }
      await actualizarEstadoProceso(true);
      if (onFinish) {
        onFinish();
      }
      return true;
    } catch (error) {
      console.error("Error al finalizar el registro de la Solicitud:", error);
      return false;
    }
    // if (canalizarSolicitud) {
    //   if (!obserAutorizacion.trim()) {
    //     setErrorObservacion("La observación es obligatoria cuando se asigna a coordinadores");
    //     return false;
    //   }
    //   try {
    //     await ProcesosApi.operarProceso({
    //       id_proceso: procesoId,
    //       operacion: "VALIDAR_PROCESO",
    //       obser_validacion: obserAutorizacion,
    //     });
    //     if (onFinish) {
    //       onFinish();
    //     }
    //     return true;
    //   } catch (error) {
    //     console.error("Error al finalizar el registro de la Solicitud:", error);
    //     return false;
    //   }
    // }
    // return true;
  };

  // Exponemos handleFinish a travÃ©s del useImperativeHandle
  React.useImperativeHandle(ref, () => ({
    handleFinish,
  }));

  const handleGeneratePdf = async () => {
    if (!procesoData?.identificador) {
      console.error("No se encontró el Identificador del Proceso");
      return;
    }
    try {
      setIsGeneratingPdf(true);
      const pdfResponse = await PDFApi.generateDocument("solicitud", procesoId, {
        responseType: "blob",
      });
      const pdfBlob = new Blob([pdfResponse.data], { type: "application/pdf" });
      if (pdfExists) {
        const anexoResponse = await ProcesosApi.obtenerAnexoProcesoByTipo(procesoData.id, {
          tipo_anexo: ANEXO_TYPE,
        });
        const anexo = anexoResponse.data;
        if (anexo != undefined && !anexoResponse.data.error) {
          await ProcesosApi.eliminarAnexoProceso(procesoData.id, anexo.id);
        }
      }
      const formData = new FormData();
      formData.append("file", pdfBlob, FILE_NAME);
      formData.append("file_type", "proceso");
      formData.append("proceso_id", procesoData.identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", ANEXO_TYPE);
      formData.append("nombre_anexo", "PDF Solicitud");
      await BucketApi.uploadFile(formData);
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
      setPdfExists(true);
      setShowPdfViewer(true);
    } catch (error) {
      console.error("Error en el proceso de generación de PDF:", error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleSetCanalizarSolicitud = (event) => {
    setCanalizarSolicitud(event.target.checked);
  };

  const hcObserAutorizacion = (event) => {
    setObserAutorizacion(event.target.value);
  };

  const handleApoderadoSelect = async (selectedApoderado) => {
    try {
      setApoderadoDeudor(selectedApoderado);
      const updatedProceso = {
        ...procesoData,
        apoderado: selectedApoderado.id,
      };
      await ProcesosApi.actualizarProceso(procesoData.id, updatedProceso);
      setProcesoData(updatedProceso);
      setShowApoderadoModal(false);
    } catch (error) {
      console.error("Error al asignar el apoderado:", error);
      alert(
        obtenerMensajeError(error, "Error al asignar el apoderado. Por favor, intenta nuevamente.")
      );
    }
  };

  const handleRemoveApoderado = async () => {
    const confirmacion = window.confirm("¿Está seguro de que desea eliminar el apoderado?");
    if (!confirmacion) return;
    try {
      const updatedProceso = {
        ...procesoData,
        apoderado: null,
      };
      await ProcesosApi.actualizarProceso(procesoData.id, updatedProceso);
      setApoderadoDeudor(null);
      setProcesoData(updatedProceso);
    } catch (error) {
      console.error("Error al eliminar el apoderado:", error);
      alert(
        obtenerMensajeError(error, "Error al eliminar el apoderado. Por favor, intenta nuevamente.")
      );
    }
  };

  return (
    <Card>
      <SuiBox sx={{ mt: 2, ml: 2 }}>
        <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 2 }}>
          <Grid size={4}>
            <SuiTypography variant="h4" fontWeight="medium">
              PDF de la Solicitud
            </SuiTypography>
          </Grid>
          <Grid size={4}>
            <SuiTypography variant="h4" fontWeight="medium">
              Validación de la Solicitud
            </SuiTypography>
          </Grid>
          <Grid size={4}>
            <SuiTypography variant="h4" fontWeight="medium">
              Comprobante de Pago
            </SuiTypography>
          </Grid>
        </Grid>
        <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={4}>
            {!pdfExists ? (
              <SuiButton
                variant="gradient"
                buttonColor="dark"
                onClick={handleGeneratePdf}
                disabled={isGeneratingPdf}
                startIcon={<PictureAsPdfIcon />}
              >
                {isGeneratingPdf ? "Generando..." : "Generar PDF"}
              </SuiButton>
            ) : (
              <>
                <SuiButton
                  variant="gradient"
                  buttonColor="dark"
                  onClick={() => setShowPdfViewer(true)}
                  startIcon={<VisibilityIcon />}
                  //sx={{ mr: 2 }}
                >
                  Ver PDF
                </SuiButton>{" "}
                <SuiButton
                  variant="gradient"
                  color="secondary"
                  onClick={handleGeneratePdf}
                  disabled={isGeneratingPdf}
                  startIcon={<PictureAsPdfIcon />}
                  sx={{ mr: 2 }}
                >
                  {isGeneratingPdf ? "Regenerando..." : "Regenerar PDF"}
                </SuiButton>
              </>
            )}
          </Grid>
          <Grid size={4}>
            <SuiButton
              variant="gradient"
              buttonColor="info"
              onClick={handleValidarSolicitud}
              disabled={isValidating}
              //sx={{ ml: 2 }}
            >
              {isValidating ? "Validando..." : "Validar"}
            </SuiButton>
          </Grid>
          <Grid size={4}></Grid>
        </Grid>
        <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mt: 2, mb: 3 }}>
          <Grid size={4}>
            {verificationSteps.map((step, index) => {
              const stepStatus = stepsStatus[index] || {};
              const isCurrentlyValidating = currentStep === index && isValidating;
              return (
                <Step key={step.key} expanded>
                  <StepLabel
                    error={stepStatus.error}
                    StepIconComponent={(props) => (
                      <StepIcon
                        {...props}
                        completed={stepStatus.completed}
                        error={stepStatus.error}
                        active={isCurrentlyValidating}
                      />
                    )}
                  >
                    <SuiTypography variant="button" fontWeight="medium">
                      {step.label}
                    </SuiTypography>
                  </StepLabel>
                  <StepContent>
                    <Box>
                      <SuiTypography variant="caption" color="text">
                        {step.description}
                      </SuiTypography>
                      {stepStatus.results && <StepResult status={stepStatus} type={step.type} />}
                    </Box>
                  </StepContent>
                </Step>
              );
            })}
          </Grid>
          <Grid size={4}>
            {validacionFallida && (
              <>
                <Checkbox
                  checked={canalizarSolicitud}
                  onChange={handleSetCanalizarSolicitud}
                  disabled={procesoData?.validado && procesoData?.estado === 1}
                />
                <SuiTypography
                  variant="button"
                  fontWeight="regular"
                  onClick={
                    procesoData?.validado && procesoData?.estado === 1
                      ? null
                      : handleSetCanalizarSolicitud
                  }
                  customClass="cursor-pointer user-select-none"
                >
                  ¿Asignación a Coordinadores?
                </SuiTypography>
              </>
            )}
            <SuiTypography variant="h6" fontWeight="light">
              Observación de Validación
            </SuiTypography>
            <SuiInput
              multiline
              rows={4}
              value={obserAutorizacion}
              onChange={hcObserAutorizacion}
              fullWidth
              error={!!errorObservacion}
              helperText={errorObservacion}
            />
          </Grid>
          <Grid size={4}>
            <SuiBox>
              <SuiTypography variant="body2" fontWeight="medium" gutterBottom>
                {planPagoData
                  ? `Plan de Pago: ${planPagoData.estado_plan || "Disponible"}`
                  : "Plan de Pago cargando..."}
              </SuiTypography>
              {planPagoData && (
                <Box>
                  {comprobanteError && (
                    <Alert severity="error" sx={{ mb: 1, fontSize: "0.75rem" }}>
                      {comprobanteError}
                    </Alert>
                  )}
                  {comprobanteSuccess && (
                    <Alert severity="success" sx={{ mb: 1, fontSize: "0.75rem" }}>
                      {comprobanteSuccess}
                    </Alert>
                  )}
                  {comprobantesExistentes.length > 0 ? (
                    <Box mb={2}>
                      <SuiTypography variant="caption" fontWeight="medium" gutterBottom>
                        Comprobantes existentes ({comprobantesExistentes.length}):
                      </SuiTypography>
                      <br />
                      {comprobantesExistentes.map((comprobante, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={0.5} mb={0.5}>
                          <Chip
                            label={`comprobante_pago_${comprobante.displayName}`}
                            onClick={() => handleViewComprobante(comprobante)}
                            clickable
                            size="small"
                            color="success"
                            variant="outlined"
                            icon={<VisibilityIcon fontSize="small" />}
                            sx={{ flexGrow: 1 }}
                          />
                          <IconButton
                            onClick={() => handleDeleteComprobante(comprobante)}
                            size="small"
                            color="error"
                            title="Eliminar comprobante"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <SuiTypography variant="caption" color="textSecondary">
                      No hay comprobantes de pago asociados a este proceso
                    </SuiTypography>
                  )}
                  <Box display="flex" flexDirection="column" gap={1}>
                    <input
                      type="file"
                      accept=".pdf"
                      id="comprobante-upload"
                      style={{ display: "none" }}
                      onChange={handleComprobanteFileChange}
                      disabled={uploadingComprobante}
                    />
                    <label htmlFor="comprobante-upload">
                      <Button
                        component="span"
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        disabled={uploadingComprobante}
                        size="small"
                        fullWidth
                      >
                        Seleccionar Comprobante
                      </Button>
                    </label>
                    {comprobanteFile && (
                      <Chip
                        label={`${comprobanteFile.name} (${(
                          comprobanteFile.size /
                          1024 /
                          1024
                        ).toFixed(2)} MB)`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    <SuiButton
                      variant="gradient"
                      buttonColor="info"
                      onClick={handleUploadComprobante}
                      disabled={!comprobanteFile || uploadingComprobante}
                      startIcon={
                        uploadingComprobante ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <AttachMoneyIcon />
                        )
                      }
                      size="small"
                    >
                      {uploadingComprobante ? "Subiendo..." : "Subir Comprobante"}
                    </SuiButton>
                  </Box>
                </Box>
              )}
            </SuiBox>
          </Grid>
        </Grid>
        <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mt: 2, mb: 3 }}>
          <Grid size={4}>
            <SuiTypography variant="h4" fontWeight="medium">
              Apoderado del Deudor
              <IconButton
                color="primary"
                onClick={() => setShowApoderadoModal(true)}
                size="medium"
                sx={{ ml: 1 }}
              >
                <EditIcon />
              </IconButton>
              {apoderadoDeudor && (
                <IconButton
                  color="error"
                  onClick={handleRemoveApoderado}
                  size="medium"
                  sx={{ ml: 1 }}
                  title="Eliminar apoderado"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </SuiTypography>
          </Grid>
          <Grid size={8}>
            {apoderadoDeudor ? (
              <SuiTypography variant="h4" fontWeight="light">
                {apoderadoDeudor.nombre_completo} ( {apoderadoDeudor.tipo_identificacion} -{" "}
                {apoderadoDeudor.identificacion} )
              </SuiTypography>
            ) : (
              <SuiTypography variant="h4" fontWeight="light">
                Sin apoderado asignado
              </SuiTypography>
            )}
          </Grid>
        </Grid>
        <SelectApoderado
          open={showApoderadoModal}
          onClose={() => setShowApoderadoModal(false)}
          onSelect={handleApoderadoSelect}
        />
        <FileDialog
          open={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          title="Vista previa del PDF"
          content={
            <embed
              src={pdfUrl}
              type="application/pdf"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          }
        />
      </SuiBox>
    </Card>
  );
});

VerificacionForm.propTypes = {
  procesoId: PropTypes.number.isRequired,
  onFinish: PropTypes.func,
};

export default VerificacionForm;
